import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { CreateUserStepsService, DraftApiService } from '@core';
import { ButtonComponent } from '@shared';

import { CREATE_USER_STEPS, CreateUserFlowStepsByRole } from '@features/users/constants';
import {
    AllKeys,
    CreateTerritoryStepRoles,
    CreateUserFlowDraft,
    UiDraft,
    UserRoles,
} from '@features/users/interfaces';

@Component({
    selector: 'app-drafts',
    imports: [ButtonComponent, DatePipe],
    templateUrl: './drafts.component.html',
    styleUrl: './drafts.component.scss',
})
export class DraftsComponent implements OnInit {
    /**
     * Inject draft service
     */
    private draftService = inject(DraftApiService);

    /**
     * Inject create user to allow us to programmatically navigate to a specific step in the create user flow
     */
    private createUserStepsService = inject(CreateUserStepsService);

    /**
     * Stored drafts signal
     */
    protected drafts = signal<UiDraft<keyof CreateUserFlowDraft>[]>([]);
    /**
     * Current selected draft id from the loaded drafts
     *
     * @remarks
     * When the user clicks a draft from the draft modal this automatically gets fed
     *
     * If no selected draft yet, it will return -1
     */
    protected currentSelectedDraftId = computed(() => {
        const role = this.createUserStepsService.selectedUserType()?.id;
        if (!role || role === 'none') return -1;

        const draftId = this.createUserStepsService.getDraft(role).id;
        return draftId ?? -1;
    });

    /**
     * Emit to close the draft modal
     */
    public closeModal = output<void>();

    /**
     * Initialize drafts on component mount
     */
    ngOnInit(): void {
        this.loadDrafts();
    }

    /**
     * Load drafts from local storage
     */
    private async loadDrafts(): Promise<void> {
        const storedDrafts = await this.draftService.getUserDrafts();
        if (storedDrafts) {
            try {
                if (Array.isArray(storedDrafts)) {
                    // Sort by createdAt descending
                    // But make the current being edited draft at the very top
                    // If no draft is selected it then sort by date, otherwise take the current being edited draft and append at the top
                    const currentDraftId = this.currentSelectedDraftId();
                    const currentEditingDraft = storedDrafts.find((sd) => sd.id === currentDraftId);

                    const filteredStoredDrafts = currentDraftId
                        ? storedDrafts
                              .filter((sd) => sd.id !== currentDraftId)
                              .sort((a, b) => {
                                  return (
                                      new Date(b.createdAt).getTime() -
                                      new Date(a.createdAt).getTime()
                                  );
                              })
                        : storedDrafts.sort((a, b) => {
                              return (
                                  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                              );
                          });

                    if (currentEditingDraft) {
                        this.drafts.set([currentEditingDraft, ...filteredStoredDrafts]);
                    } else {
                        this.drafts.set(filteredStoredDrafts);
                    }
                }
            } catch (error) {
                console.error('Error parsing drafts:', error);
            }
        }
    }

    /**
     * Selects a draft and navigates to the last populated step in the create user flow.
     *
     * @param id - The ID of the draft to select
     *
     * @remarks
     * This method performs the following steps:
     * 1. Finds the draft by ID from the current list of drafts
     * 2. Early exits if the selected draft is already the one being edited
     * 3. Loads the draft state into the create user service
     * 4. Determines the last populated step by cross-referencing the draft keys
     *    against the valid steps for the current user role (excluding metadata keys
     *    such as `id`, `createdAt`, and `updatedAt`)
     * 5. Navigates to the last populated step, or falls back to the first step
     *    if no populated steps are found
     * 6. Closes the draft selection modal
     *
     * @example
     * // Selecting a dealer draft with 'input-information' filled
     * // will navigate directly to 'input-information' step
     * this.selectDraft('9db08322-f544-43d0-a3bd-aff8c622163c');
     */
    protected selectDraft(id: string): void {
        const draft = this.drafts().find((draft) => draft.id === id);

        if (!draft) return;

        // If the selected draftId is the current being edited draft then we just close the modal
        if (id === this.currentSelectedDraftId()) {
            this.closeModal.emit();
            return;
        }

        // 1. Get the role from the saved draft metadata
        const role = draft.role as UserRoles;

        // 2. Load the draft into the service state
        this.createUserStepsService.loadDraft(role, draft as Record<string, any>);

        // 3. Determine the last incomplete step to navigate to
        // We'll filter the draft keys that are valid steps for the role and have data
        const roleSteps = CreateUserFlowStepsByRole[role as keyof typeof CreateUserFlowStepsByRole];
        const populatedStepsInDraft = Object.keys(draft).filter(
            (key) =>
                !['id', 'createdAt', 'updatedAt', 'role'].includes(key) && roleSteps.includes(key),
        );

        const lastPopulatedStep =
            populatedStepsInDraft[populatedStepsInDraft.length - 1] || roleSteps[0];

        // 4. Validate the selected draft data
        this.createUserStepsService.validateDraftDataOnLoad(populatedStepsInDraft);

        // 5. Close the draft modal
        this.closeModal.emit();
    }

    /**
     * Delete a specific draft
     * @param id The ID of the draft to delete
     */
    protected deleteDraft(id: string): void {
        const currentDrafts = this.drafts();
        const updatedDrafts = currentDrafts.filter((draft) => draft.id !== id);

        localStorage.setItem('draft', JSON.stringify(updatedDrafts));
        this.drafts.set(updatedDrafts);

        // If the deleted draft is the current being edited draft then we just close the modal
        // then we need to clear the current draft from the create user service
        if (id === this.currentSelectedDraftId()) {
            this.closeModal.emit();
            this.createUserStepsService.clearDraft();
        }
    }

    /**
     * A utility function to convert the camelCase string into a proper string format
     *
     * @param {string} str The string value to convert to
     * @returns A properly formatted string seperated by space if multiple words
     */
    protected splitCamelCase(str: string): string {
        return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (s) => s.toUpperCase()); // capitalize first letter
    }
}
